import { Object3D, Vector3, Vector3Tuple } from 'three';
import { ProductRenderer } from '../ProductRenderer';
import {
	AccessorySlotsData,
	AccessoryTranslationType,
	CustomObjectName,
	ModelSlotsData,
	ThreeEvent,
	UserDataProp,
} from '../types';

export class ProductManager {
	private slotsData: ModelSlotsData = {};
	private model: Object3D | null = null;

	constructor(private renderer: ProductRenderer) {}

	initProduct(model: Object3D) {
		this.model = model;
		this.renderer.product.add(this.model);
		this.setSlotsData();
	}

	clearModel() {
		this.renderer.product.remove(this.model!);
		this.model = null;
	}

	private setSlotsData() {
		this.slotsData = {};

		const slots = this.model!.getObjectByName(CustomObjectName.SLOTS);
		if (!slots) throw new Error('MISSING_SLOTS_OBJECT');

		for (const child of slots?.children) {
			const translationAxes: AccessorySlotsData['translationAxes'] = child
					.userData[UserDataProp.TRANSLATION_AXES] || [
					AccessoryTranslationType.NONE,
					AccessoryTranslationType.NONE,
					AccessoryTranslationType.NONE,
				],
				translationSteps = child.userData[UserDataProp.TRANSLATION_STEPS]
					? child.userData[UserDataProp.TRANSLATION_STEPS]
					: [0.01, 0.01, 0.01],
				slots: AccessorySlotsData['slots'] = [],
				defaultSlotsIds: [number, number, number] = child.userData[
					UserDataProp.DEFAULT_SLOTS
				] || [0, 0, 0];

			const objectName = child.name.replace(/_slots$/, '');

			const object = this.model!.getObjectByName(objectName);
			if (!object) throw new Error(`MISSING_OBJECT: ${objectName}`);

			const initialPosition = object.getWorldPosition(new Vector3()).toArray();

			let min: Vector3Tuple = [0, 0, 0],
				max: Vector3Tuple = [0, 0, 0],
				accessoriesUpperBoundLimitY: AccessorySlotsData['accessoriesUpperBoundLimitY'];

			const minObj = child.getObjectByName(`${child.name}_min`),
				maxObj = child.getObjectByName(`${child.name}_max`),
				upperBoundObj = child.getObjectByName(`${child.name}_upper_bound`);

			if (minObj) min = minObj.getWorldPosition(new Vector3()).toArray();
			if (maxObj) max = maxObj.getWorldPosition(new Vector3()).toArray();

			if (upperBoundObj) {
				const worldPos = upperBoundObj.getWorldPosition(new Vector3());
				accessoriesUpperBoundLimitY = worldPos.y;
			}

			child.traverse((slotChild) => {
				const slotName = slotChild.userData[UserDataProp.SLOT_NAME];
				if (slotName === undefined) return;

				const slotAxis = slotChild.userData[UserDataProp.SLOT_AXIS];
				if (slotAxis === undefined) return;

				const slotId = slotChild.userData[UserDataProp.SLOT_ID];
				if (slotId === undefined) return;

				const position = slotChild.getWorldPosition(new Vector3()).toArray();
				slots.push({
					name: slotName,
					position,
					slug: slotChild.name,
					axis: slotAxis,
					id: slotId,
				});
			});

			const defaultSlots = translationAxes.map((translationType, idx) => {
				if (translationType !== AccessoryTranslationType.SLOTS) return null;

				const defaultSlot = slots.find(
					(slot) => slot.axis === idx && slot.id === defaultSlotsIds[idx]
				);

				return defaultSlot || null;
			}) as AccessorySlotsData['defaultSlots'];

			this.slotsData[objectName] = {
				translationAxes,
				translationSteps,
				min,
				max,
				slots,
				defaultSlots,
				initialPosition,
				accessoriesUpperBoundLimitY,
			};
		}

		this.model!.traverse((child) => {
			if (child.userData[UserDataProp.IS_ACCESSORY]) child.visible = false;
			if (child.userData[UserDataProp.IS_HIDDEN]) child.visible = false;
		});

		this.renderer.emitter.emit(
			ThreeEvent.SET_SLOTS_DATA,
			structuredClone(this.slotsData)
		);
	}

	getAccessoryObject(slug: string) {
		const object = this.model!.getObjectByName(slug);
		if (!object) throw new Error(`MISSING_OBJECT: ${slug}`);

		const clone = object.clone();

		return clone;
	}
}
